import { useEffect, useRef, useState } from 'react';

import { CallHistoryTab } from './CallHistoryTab';
import { ConnectionTab } from './ConnectionTab';
import { LiveStatsTab } from './LiveStatsTab';
import { MediaTracksTab } from './MediaTracksTab';
import { SessionTab } from './SessionTab';
import {
  CloseButton,
  DebugContainer,
  FontSizeButton,
  FontSizeControls,
  TabBar,
  TabButton,
  ToolbarRow,
} from './styles';

const MENU_KEY = '*614';
const debugMenuVisible = Boolean(import.meta.env.VITE_APP_DEBUG_MENU_VISIBLE);

const TABS = [
  { id: 'session', label: 'Session' },
  { id: 'connection', label: 'Connection' },
  { id: 'media', label: 'Media' },
  { id: 'stats', label: 'Live Stats' },
  { id: 'calls', label: 'Calls' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const FONT_SIZES = [10, 12, 14, 16, 18, 20, 22] as const;
const DEFAULT_FONT_INDEX = 3;

export const HiddenDebugMenu = () => {
  const [isVisible, setIsVisible] = useState(debugMenuVisible);
  const [activeTab, setActiveTab] = useState<TabId>('session');
  const [fontSizeIndex, setFontSizeIndex] = useState(DEFAULT_FONT_INDEX);
  const typedKeysRef = useRef('');

  const fontSize = FONT_SIZES[fontSizeIndex];

  const decreaseFont = () => setFontSizeIndex((i) => Math.max(0, i - 1));
  const increaseFont = () =>
    setFontSizeIndex((i) => Math.min(FONT_SIZES.length - 1, i + 1));
  const closeMenu = () => setIsVisible(false);

  useEffect(function keyListener() {
    const handleKeyDown = (event: KeyboardEvent) => {
      const next = (typedKeysRef.current + event.key).slice(-MENU_KEY.length);

      if (next === MENU_KEY) {
        setIsVisible((isMenuVisible) => !isMenuVisible);
        typedKeysRef.current = '';
        return;
      }

      typedKeysRef.current = next;
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <DebugContainer elevation={4} style={{ fontSize: `${fontSize}px` }}>
      <ToolbarRow>
        <TabBar>
          {TABS.map((tab) => (
            <TabButton
              key={tab.id}
              $active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </TabButton>
          ))}
        </TabBar>
        <FontSizeControls>
          <FontSizeButton onClick={decreaseFont} disabled={fontSizeIndex === 0}>
            A−
          </FontSizeButton>
          <FontSizeButton
            onClick={increaseFont}
            disabled={fontSizeIndex === FONT_SIZES.length - 1}
          >
            A+
          </FontSizeButton>
        </FontSizeControls>
        <CloseButton aria-label='Close debug menu' onClick={closeMenu}>
          x
        </CloseButton>
      </ToolbarRow>

      {activeTab === 'session' && <SessionTab />}
      {activeTab === 'connection' && <ConnectionTab />}
      {activeTab === 'media' && <MediaTracksTab />}
      {activeTab === 'stats' && <LiveStatsTab />}
      {activeTab === 'calls' && <CallHistoryTab />}
    </DebugContainer>
  );
};
